import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Select, Store } from '@ngxs/store';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { DynamicDialogRef } from 'primeng/dynamicdialog/dynamicdialog-ref';
import { combineLatest, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { TrustBadgeService } from '../../../services/trust-badge.service';
import { ApkSignPolicyDialogComponent } from '../../apk/apk-sign-policy-dialog/apk-sign-policy-dialog.component';
import { DocumentsUploadedDialogComponent } from '../../apk/documents-uploaded-dialog/documents-uploaded-dialog.component';
import {
  DocumentBox,
  DocumentTypeEnum,
} from '../../cta/models/document-box-interface';
import { PolicyCreatorService } from '../../policy-creator/policy-creator.service';
import {
  GetPolicyByNumber,
  ResetOfferState,
} from '../../policy-creator/store/offer.action';
import { OfferState } from '../../policy-creator/store/offer.state';
import { ResetPolicyCreatorState } from '../../policy-creator/store/policy-creator.actions';
import { CreationLockResponse } from '../../shared/interfaces/creation-lock-response.interface';
import { SetWarrantyFlag } from '../../warranty-creator/store/warranty-creator.actions';
import { WarrantyOfferState } from '../../warranty-creator/store/warranty-offer.state';

enum PortfolioType {
  GAP = 'GAP',
  WARRANTY = 'WARRANTY',
}

@Component({
  selector: 'app-documents-upload',
  templateUrl: './documents-upload.component.html',
  styleUrls: ['./documents-upload.component.scss'],
  providers: [DialogService],
})
export class DocumentsUploadComponent implements OnInit, OnDestroy {
  documentsHeader1: string;
  documentsHeader2: string;
  policyId: number;
  policyNo: string;
  documentBoxes: DocumentBox[];
  documentsUploaded = false;
  isLoading = true; // Default to loading state
  @Select(OfferState.offerLocked)
  offerLocked$: Observable<CreationLockResponse>;
  @Select(WarrantyOfferState.warrantyOfferLocked)
  warrantyOfferLocked$: Observable<CreationLockResponse>;
  isWarrantyGap: boolean;
  portfolioType: PortfolioType;
  authModalOpen = false;

  private ngDestroy$: Subject<void> = new Subject();
  private dialogRef: DynamicDialogRef;

  constructor(
    private translocoService: TranslocoService,
    private activatedRoute: ActivatedRoute,
    private messageService: MessageService,
    private router: Router,
    private policyCreatorService: PolicyCreatorService,
    private store: Store,
    private trustBadgeService: TrustBadgeService,
    public dialogService: DialogService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.policyNo = this.activatedRoute.snapshot.paramMap.get('policyNo');
    // first I have to determine if its classic gap or warranty gap
    this.activatedRoute.queryParamMap.pipe(take(1)).subscribe((q) => {
      this.portfolioType =
        q.get('classicGap') === 'true'
          ? PortfolioType.GAP
          : PortfolioType.WARRANTY;
      this.isWarrantyGap = this.portfolioType === PortfolioType.WARRANTY;
    });

    // second, fetch the policy from API
    this.store
      .dispatch(new GetPolicyByNumber(this.policyNo))
      .pipe(take(1))
      .subscribe(() => {
        this.store.dispatch(new SetWarrantyFlag(this.isWarrantyGap));
      });

    if (this.policyNo) {
      if (!this.policyNo) {
        console.error('Missing policyNo');
        return;
      }

      this.getUploadBoxes();
      // Open dialog for sign policy
      combineLatest([this.offerLocked$, this.warrantyOfferLocked$])
        .pipe(takeUntil(this.ngDestroy$))
        .subscribe(([offerLocked, warrantyOfferLocked]) => {
          if (!offerLocked && !warrantyOfferLocked) {
            return;
          }

          // Data has been loaded, set loading to false
          this.isLoading = false;

          const currentOffer = offerLocked || warrantyOfferLocked;

          this.isWarrantyGap = currentOffer.productCode.includes('5_CP_');
          this.setHeader(this.isWarrantyGap);

          if (currentOffer?.signatureState === 'confirmed') {
            if (environment.production) {
              this.initTrustBadge();
            }
          }

          console.log('currentOffer', currentOffer);
          // signatureState: "waiting_for_confirmation"
          // signatureTypeCode: "AUTHORIZED_BY_SMS"
          if (
            currentOffer.signatureState === 'waiting_for_confirmation' &&
            currentOffer.signatureTypeCode === 'AUTHORIZED_BY_SMS' &&
            !this.authModalOpen
          ) {
            this.openAuthorizationModal();
            this.authModalOpen = true;
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  checkUploadStatus(): void {
    this.documentsUploaded = false;
    this.documentBoxes.forEach((documentBox, index) => {
      if (documentBox.uploaded === false) {
        return;
      }

      if (
        index === this.documentBoxes.length - 1 &&
        documentBox.uploaded === true
      ) {
        this.documentsUploaded = true;
      }
    });

    if (this.documentsUploaded) {
      this.store.dispatch([
        new ResetOfferState(),
        new ResetPolicyCreatorState(),
      ]);
      const uploadedDialog = this.dialogService.open(
        DocumentsUploadedDialogComponent,
        {
          data: {
            isWarranties: this.isWarrantyGap,
          },
        },
      );

      uploadedDialog.onClose.subscribe(() => {
        if (this.isWarrantyGap) {
          window.location.replace(environment.apkUrl);
        }
        this.router.navigate(['/']);
      });
    }
  }

  getUploadBoxes(): void {
    combineLatest([this.offerLocked$, this.warrantyOfferLocked$])
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(([offerLocked, warrantyOfferLocked]) => {
        if (!offerLocked && !warrantyOfferLocked) {
          return;
        }

        // Data has been loaded, set loading to false
        this.isLoading = false;

        this.policyNo = offerLocked?.number || warrantyOfferLocked?.number;
        const productCode =
          offerLocked?.productCode || warrantyOfferLocked?.productCode;

        if (productCode.includes('5_CP_')) {
          this.documentBoxes = [
            {
              documentBoxType: DocumentTypeEnum.registryNo,
              documentBoxTitle: this.translocoService.translate(
                'documentsUpload.documentBox.registryNo.title',
              ),
            },
            {
              documentBoxType: DocumentTypeEnum.policy,
              documentBoxTitle: this.translocoService.translate(
                'documentsUpload.documentBox.policy.titleWarrantyPolicy',
              ),
            },
            {
              documentBoxType: DocumentTypeEnum.invoice,
              documentBoxTitle: this.translocoService.translate(
                'documentsUpload.documentBox.invoice.title',
              ),
            },
          ];
        } else {
          this.documentBoxes = [
            {
              documentBoxType: DocumentTypeEnum.registryNo,
              documentBoxTitle: this.translocoService.translate(
                'documentsUpload.documentBox.registryNo.title',
              ),
            },
            {
              documentBoxType: DocumentTypeEnum.policy,
              documentBoxTitle: this.translocoService.translate(
                warrantyOfferLocked
                  ? 'documentsUpload.documentBox.policy.titleWarranty'
                  : 'documentsUpload.documentBox.policy.title',
              ),
            },
            {
              documentBoxType: DocumentTypeEnum.invoice,
              documentBoxTitle: this.translocoService.translate(
                'documentsUpload.documentBox.invoice.title',
              ),
            },
          ];
        }
      });
  }

  initTrustBadge(): void {
    this.trustBadgeService
      .load({
        name: 'app-component',
        src: `https://widgets.trustedshops.com/js/${environment.trustBadgeId}.js`,
      })
      .pipe(take(1))
      .subscribe();
  }

  setHeader(isWarrantyGap: boolean) {
    this.translocoService
      .selectTranslate('documentsUpload.thankYou.header1')
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(() => {
        this.documentsHeader1 = this.translocoService.translate(
          isWarrantyGap
            ? 'documentsUpload.thankYou.header1Warranty'
            : 'documentsUpload.thankYou.header1',
        );
        this.documentsHeader2 = this.translocoService.translate(
          isWarrantyGap
            ? 'documentsUpload.thankYou.header2Warranty'
            : 'documentsUpload.thankYou.header2',
        );
      });
  }

  openAuthorizationModal(): void {
    combineLatest([this.offerLocked$, this.warrantyOfferLocked$])
      .pipe(take(1))
      .subscribe(([offerLocked, warrantyOfferLocked]) => {
        const currentOffer = offerLocked || warrantyOfferLocked;

        if (currentOffer) {
          this.dialogRef = this.dialogService.open(
            ApkSignPolicyDialogComponent,
            {
              data: {
                offerLocked: currentOffer,
              },
            },
          );
        }
      });
  }
}
